import { FC } from 'react'
import { Navigate, Outlet, useMatches } from 'react-router-dom'
import { useAuthenticationContext } from '../../auth/AuthenticationContextProvider'
import { isPathAuthorized, isRouteAuthHandle } from '../../auth/routeAuth'
import { UnauthorizedPage } from '../../pages/UnauthorizedPage'
import { ALL_MODES, isModeRouteHandle } from '../../pages/engagement-review/ErtPage/EngagementDetailsEnums'

/**
 * A single layout component that enforces scope-based authorization for all matched routes.
 *
 * Place this as the element on a layout route that wraps all protected routes.
 * Each child route declares its auth requirements via the `handle` property:
 *
 * ```
 * {
 *   path: 'foo',
 *   element: <FooPage />,
 *   handle: { auth: { scopes: [READ_SCOPE], mode: 'all' } }
 * }
 * ```
 *
 * AuthMiddleware reads all matched route handles via useMatches() and renders
 * the UnauthorizedPage inline (without changing the URL) if any auth check
 * in the matched route chain fails.
 *
 * A failing route whose handle also identifies it as one of a set of mode routes
 * (via `handle: { mode: ModeType }`, e.g. ERT's QA/Inspect/Care modes) is treated as a
 * soft failure: instead of unauthorizing immediately, we redirect to the first sibling
 * mode the user is authorized for, and only fall back to UnauthorizedPage if none exist.
 */
export const AuthMiddleware: FC = () => {
  const matches = useMatches()
  const authContext = useAuthenticationContext()

  for (const [index, match] of matches.entries()) {
    if (isRouteAuthHandle(match.handle)) {
      const { scopes, mode } = match.handle.auth
      const hasAccess = mode === 'all' ? authContext.hasAllScopes(scopes) : authContext.hasAnyScope(scopes)
      if (!hasAccess) {
        if (isModeRouteHandle(match.handle)) {
          const parentPathname = matches[index - 1]?.pathname
          const fallbackMode =
            parentPathname && ALL_MODES.find(candidateMode => isPathAuthorized(`${parentPathname}/${candidateMode.toLowerCase()}`, authContext))
          if (fallbackMode) {
            return <Navigate to={`${parentPathname}/${fallbackMode.toLowerCase()}`} replace={true} />
          }
        }
        return <UnauthorizedPage />
      }
    }
  }

  return <Outlet />
}
